import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useStands } from '../context/StandsContext';
import { MapPin, ArrowLeft } from 'lucide-react';
import ExportButton from './ExportButton';
import MaintenanceButton from './public/MaintenanceButton';
import MaintenanceRequestModal from './MaintenanceRequestModal';

// Helper local pour parser des dates hétérogènes (string | number | Date)
const parseDate = (d: any): Date | undefined => {
  try {
    if (!d) return undefined;
    if (d instanceof Date) return isNaN(d.getTime()) ? undefined : d;
    const n = typeof d === 'number' ? new Date(d) : new Date(String(d));
    return isNaN(n.getTime()) ? undefined : n;
  } catch {
    return undefined;
  }
};

const StandDetailPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { stands, publications, getLowStockPublications } = useStands();
  // (features comme date d'installation pourront être réactivées plus tard)
  const [showMaintenanceRequestModal, setShowMaintenanceRequestModal] = useState(false);

  const stand = stands.find(s => s.id === id);

  if (!stand) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="card p-8 max-w-md w-full text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Présentoir non trouvé
          </h2>
          <p className="text-gray-600 mb-6">
            Le présentoir demandé n'existe pas ou n'est plus disponible.
          </p>
          <button
            onClick={() => navigate('/')}
            className="btn btn-primary inline-flex items-center"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour au tableau de bord
          </button>
        </div>
      </div>
    );
  }

  // Infos installation + maintenance (robuste aux champs optionnels)
  const installedAt = parseDate((stand as any).installedAt);
  const ageInDays = installedAt
    ? Math.floor((Date.now() - installedAt.getTime()) / (1000 * 60 * 60 * 24))
    : undefined;

  const maintenanceHistory = ((stand as any).maintenanceHistory || []) as any[];
  const lastMaintenanceRaw = maintenanceHistory.length ? maintenanceHistory[maintenanceHistory.length - 1] : undefined;
  const lastMaintenanceDate = lastMaintenanceRaw ? parseDate(lastMaintenanceRaw.date) : undefined;
  const nextDue = lastMaintenanceDate ? new Date(lastMaintenanceDate) : undefined;
  if (nextDue) {
    nextDue.setMonth(nextDue.getMonth() + 3); // échéance simple à +3 mois
  }

  // Publications installées (essaye plusieurs formes connues)
  const rawInstalled =
    (stand as any).publications ??
    (stand as any).posters ??
    (stand as any).slots ??
    [];
  const installedIds = Array.isArray(rawInstalled)
    ? rawInstalled
    : rawInstalled && typeof rawInstalled === 'object'
    ? Object.keys(rawInstalled)
    : [];
  const installedCount = installedIds.length;

  // Publications en faible stock pour ce stand
  const lowStock = typeof getLowStockPublications === 'function'
    ? getLowStockPublications(stand.id)
    : [];

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <button
            onClick={() => navigate('/')}
            className="text-gray-600 hover:text-gray-900 mb-4 inline-flex items-center"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour au tableau de bord
          </button>
          <h1 className="text-3xl font-bold text-gray-900">{stand.name}</h1>
          <p className="text-gray-600 mt-1 flex items-center">
            <MapPin className="h-4 w-4 mr-2" />
            {stand.location}
          </p>
        </div>
        <ExportButton type="single" stand={stand} />
      </div>

      <div className="card p-6 mt-6 space-y-6">
        <h2 className="text-xl font-semibold text-gray-900">Actions maintenance</h2>
        <MaintenanceButton onClick={() => setShowMaintenanceRequestModal(true)} />
      </div>

      {/* Infos installation et maintenance */}
      <div className="card p-6 mt-6 space-y-4">
        <h2 className="text-xl font-semibold text-gray-900">Informations</h2>
        <div className="text-gray-700 space-y-1">
          {installedAt ? (
            <>
              <p>
                Installé le : <span className="font-medium">{installedAt.toLocaleDateString()}</span>
              </p>
              <p>
                Âge estimé : <span className="font-medium">{ageInDays} jours</span>
              </p>
            </>
          ) : (
            <p className="text-gray-500">Date d'installation inconnue</p>
          )}
          <div className="h-px bg-gray-200 my-2" />
          <p>
            Dernière maintenance :{' '}
            <span className="font-medium">
              {lastMaintenanceDate ? lastMaintenanceDate.toLocaleDateString() : 'jamais'}
            </span>
          </p>
          <p>
            Prochaine échéance (3 mois) :{' '}
            <span className="font-medium">
              {nextDue ? nextDue.toLocaleDateString() : 'à planifier'}
            </span>
          </p>
        </div>
      </div>

      {/* Historique maintenance */}
      <div className="card p-6 mt-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Historique maintenance</h2>
        {maintenanceHistory.length ? (
          <ul className="divide-y divide-gray-200">
            {maintenanceHistory.slice().reverse().map((m, idx) => {
              const d = parseDate((m as any).date);
              const type = (m as any).type as string | undefined;
              const notes = (m as any).notes as string | undefined;
              const userId = (m as any).userId as string | undefined;
              return (
                <li key={idx} className="py-3">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-700">
                      <span className="font-medium">{d ? d.toLocaleDateString() : 'date inconnue'}</span>
                      {type ? <> • {type}</> : null}
                      {notes ? <> — {notes}</> : null}
                    </div>
                    {userId ? <span className="text-xs text-gray-500">par {userId}</span> : null}
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="text-gray-500">Aucun historique de maintenance.</p>
        )}
      </div>

      {/* Publications et stocks */}
      <div className="card p-6 mt-6 space-y-4">
        <h2 className="text-xl font-semibold text-gray-900">Publications et stocks</h2>
        <div className="text-gray-700">
          <p>
            Publications installées :{' '}
            <span className="font-medium">
              {installedCount || (installedIds ? 0 : 'non renseigné')}
            </span>
          </p>
        </div>
        <div>
          <p className="text-gray-800 font-medium mb-2">
            Faible stock ({Array.isArray(lowStock) ? lowStock.length : 0})
          </p>
          {Array.isArray(lowStock) && lowStock.length ? (
            <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
              {lowStock.map((p: any, i: number) => (
                <li key={p?.id ?? i}>
                  {p?.title || p?.name || p?.id || 'Publication'}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-500 text-sm">Aucune publication en faible stock.</p>
          )}
        </div>
      </div>

      {showMaintenanceRequestModal && stand && (
        <MaintenanceRequestModal
          stand={stand}
          isOpen={showMaintenanceRequestModal}
          onClose={() => setShowMaintenanceRequestModal(false)}
        />
      )}

      {/* TODO: compléter le reste de la page détail (historique, publications, etc.) */}
    </div>
  );
};

export default StandDetailPage;